import { clamp } from '../core/math';
import { Rng } from '../core/rng';
import { COOK, STAGE } from '../fight/data';
import type { Bout } from '../fight/Bout';
import type { FighterPose, FighterState, FightEvent, Projectile, SideLook } from '../fight/types';
import { SHIRTS, SKIN, HAIR } from '../game/customers';
import type { CharacterLook } from '../game/types';
import { dottieSprite, miniSprite, MINI_W } from './characters';
import { ANCHOR_X, SIDE_H, sideSprite } from './fighterSide';
import { drawWaffle } from './icons';
import { Particles } from './particles';
import { drawPixelText, glyphRows } from './pixelFont';
import {
  createCanvas,
  ctx2d,
  drawSprite,
  mixColor,
  noise,
  px,
  spriteFromGrid,
  type Sprite,
} from './sprite';
import { SIGN_EDGE, SIGN_YELLOW } from './theme';

const W = STAGE.width;
const H = STAGE.height;
const FLOOR = STAGE.floorY;
const WALL_BOTTOM = 88;

const CHAIR_GRID = [
  '..kkkkkkkk..',
  '..krrrrrrk..',
  '..krRrrrrk..',
  '..krrrrrrk..',
  '..kkkkkkkk..',
  '...s....s...',
  'kkkkkkkkkkkk',
  'krrrrrrrrRRk',
  'kkkkkkkkkkkk',
  '.s..s..s..s.',
  '.s..s..s..s.',
  's...s..s...s',
];

const tints = new WeakMap<Sprite, Map<string, Sprite>>();

/** A flat-colour silhouette of a sprite (hit flashes, windup tells), cached per sprite. */
function tint(sprite: Sprite, color: string): Sprite {
  let byColor = tints.get(sprite);
  if (!byColor) {
    byColor = new Map();
    tints.set(sprite, byColor);
  }
  const hit = byColor.get(color);
  if (hit) return hit;
  const canvas = createCanvas(sprite.w, sprite.h);
  const ctx = ctx2d(canvas);
  ctx.drawImage(sprite.canvas, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, sprite.w, sprite.h);
  const out = { canvas, w: sprite.w, h: sprite.h };
  byColor.set(color, out);
  return out;
}

interface Spectator {
  look: CharacterLook;
  x: number;
  y: number;
  booth: boolean;
  filming: boolean;
  phase: number;
}

/** The Waffle House dining room, side-on, as a fighting-game stage. */
export class FightStage {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly bg: HTMLCanvasElement;
  private readonly fg: HTMLCanvasElement;
  private readonly particles = new Particles();
  private readonly chair: Sprite;
  private spectators: Spectator[] = [];
  private t = 0;
  private shake = 0;
  private hype = 0;
  private superFlash = 0;
  reducedMotion = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    canvas.width = W;
    canvas.height = H;
    this.ctx = ctx2d(canvas);
    this.bg = createCanvas(W, H);
    this.fg = createCanvas(W, H);
    this.paintBackground(ctx2d(this.bg));
    this.paintForeground(ctx2d(this.fg));
    this.chair = spriteFromGrid('fight-chair', CHAIR_GRID, {
      k: '#2a1a10',
      r: '#c0392b',
      R: '#8e2418',
      s: '#b8c0c8',
    });
    this.seatCrowd(1);
  }

  /** A fresh crowd of rubberneckers for each shift change. */
  seatCrowd(seed: number): void {
    const rng = new Rng(seed);
    const look = (): CharacterLook => ({
      species: 'human',
      skin: rng.pick(SKIN),
      hair: rng.pick(HAIR),
      hairStyle: rng.pick(['short', 'long', 'bun', 'curly', 'afro', 'buzz'] as const),
      hat: rng.chance(0.3) ? rng.pick(['trucker', 'cap', 'beanie'] as const) : 'none',
      hatColor: rng.pick(SHIRTS),
      eyes: rng.chance(0.2) ? 'glasses' : 'normal',
      facialHair: 'none',
      shirt: rng.pick(SHIRTS),
      shirtAlt: rng.pick(SHIRTS),
      shirtStyle: rng.pick(['plain', 'stripes', 'hoodie', 'flannel'] as const),
      accessory: 'none',
    });
    this.spectators = [];
    for (const bx of [58, 128, 198]) {
      this.spectators.push({
        look: look(),
        x: bx - 14,
        y: 52,
        booth: true,
        filming: rng.chance(0.35),
        phase: rng.range(0, 6),
      });
      this.spectators.push({
        look: look(),
        x: bx + 2,
        y: 52,
        booth: true,
        filming: rng.chance(0.35),
        phase: rng.range(0, 6),
      });
    }
    this.particles.clear();
    this.hype = 0.4;
  }

  handle(e: FightEvent, bout: Bout): void {
    switch (e.type) {
      case 'hit': {
        const y = FLOOR - 30;
        const colors = e.blocked
          ? ['#9ec6ff', '#ffffff']
          : e.heavy
            ? ['#fff3b0', '#ffd23f', '#ff6b4a']
            : ['#ffffff', '#ffd23f'];
        this.particles.burst(e.x, y, e.heavy ? 14 : 7, {
          colors,
          speed: e.heavy ? 90 : 60,
          life: 0.35,
          gravity: 0,
          shape: 'spark',
        });
        this.hype = Math.min(1, this.hype + (e.heavy ? 0.35 : 0.12));
        if (e.heavy && !e.blocked)
          this.particles.burst(e.x, FLOOR - 2, 6, {
            colors: ['#d9d0c4', '#bfb5a8'],
            speed: 30,
            life: 0.5,
            gravity: -10,
            up: true,
          });
        break;
      }
      case 'parry':
        this.particles.burst(e.x, FLOOR - 30, 16, {
          colors: ['#9ec6ff', '#ffffff', '#ffd23f'],
          speed: 110,
          life: 0.45,
          gravity: 0,
          shape: 'spark',
        });
        this.hype = 1;
        break;
      case 'clash':
        this.particles.burst(e.x, FLOOR - 26, 18, {
          colors: ['#ffffff', '#ffd23f', '#e7b75a'],
          speed: 90,
          life: 0.4,
          gravity: 60,
        });
        break;
      case 'shake':
        this.shake = Math.max(this.shake, e.amount);
        break;
      case 'super':
        this.superFlash = 0.9;
        this.hype = 1;
        break;
      case 'end': {
        const loser = e.outcome === 'calmed' ? bout.opponent : bout.player;
        if (e.outcome === 'calmed') {
          this.particles.burst(loser.x, FLOOR - 36, 14, {
            colors: ['#ff5fa2', '#ff8fab', '#ffffff'],
            speed: 22,
            life: 2,
            gravity: -14,
            shape: 'heart',
            up: true,
          });
        }
        this.hype = 1;
        break;
      }
      default:
        break;
    }
  }

  render(bout: Bout | null, dt: number): void {
    this.t += dt;
    const ctx = this.ctx;
    this.particles.update(dt);
    this.shake = Math.max(0, this.shake - dt);
    this.hype = Math.max(0.15, this.hype - dt * 0.35);
    this.superFlash = Math.max(0, this.superFlash - dt);

    ctx.save();
    if (this.shake > 0 && !this.reducedMotion) {
      const s = Math.ceil(this.shake * 5);
      ctx.translate(Math.round((Math.random() - 0.5) * s), Math.round((Math.random() - 0.5) * s));
    }
    ctx.clearRect(-8, -8, W + 16, H + 16);
    ctx.drawImage(this.bg, 0, 0);
    this.drawSign(ctx);
    this.drawJukebox(ctx);
    this.drawCrowd(ctx);
    this.drawDottie(ctx);

    if (this.superFlash > 0) {
      ctx.fillStyle = `rgba(10,8,20,${Math.min(0.55, this.superFlash)})`;
      ctx.fillRect(0, 0, W, H);
    }

    if (bout) {
      this.drawShadow(ctx, bout.player);
      this.drawShadow(ctx, bout.opponent);
      const oppFirst = bout.opponent.action !== null && bout.player.action === null;
      const order: FighterState[] = oppFirst
        ? [bout.player, bout.opponent]
        : [bout.opponent, bout.player];
      for (const f of order) this.drawFighter(ctx, bout, f);
      for (const p of bout.projectiles) this.drawProjectile(ctx, p);
    }
    this.particles.draw(ctx);
    ctx.drawImage(this.fg, 0, 0);
    ctx.restore();
  }

  /* ───────────────────────── static art ───────────────────────── */

  private paintBackground(ctx: CanvasRenderingContext2D): void {
    // Wall
    px(ctx, 0, 0, W, WALL_BOTTOM, '#e2bd6b');
    for (let y = 8; y < 60; y += 4) px(ctx, 0, y, W, 1, '#d8b160');
    px(ctx, 0, 0, W, 5, '#6b4a2b');
    px(ctx, 0, 5, W, 1, '#4a3220');
    // Pendant lights
    for (const lx of [64, 128, 192]) {
      px(ctx, lx, 6, 1, 6, '#2b1d14');
      px(ctx, lx - 4, 12, 9, 3, '#2b1d14');
      px(ctx, lx - 3, 15, 7, 1, '#fff3c4');
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = '#fff3c4';
      ctx.beginPath();
      ctx.moveTo(lx - 3, 16);
      ctx.lineTo(lx - 22, WALL_BOTTOM);
      ctx.lineTo(lx + 22, WALL_BOTTOM);
      ctx.lineTo(lx + 3, 16);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    // Windows onto the lot
    for (const [x, w] of [
      [32, 56],
      [100, 56],
      [168, 56],
    ] as const) {
      px(ctx, x - 2, 16, w + 4, 34, '#2b1d14');
      for (let y = 18; y < 48; y++)
        px(ctx, x, y, w, 1, mixColor('#070b1f', '#231642', (y - 18) / 30));
      for (let i = 0; i < 14; i++) {
        const sx = x + Math.floor(noise(i, x, 3) * w);
        const sy = 19 + Math.floor(noise(x, i, 3) * 16);
        px(ctx, sx, sy, 1, 1, '#ffffff');
      }
      px(ctx, x, 42, w, 6, '#1b1d24');
      for (let sx = x + 4; sx < x + w; sx += 14) px(ctx, sx, 44, 1, 4, '#c9a93a');
      px(ctx, x + Math.floor(w / 2), 18, 1, 30, '#2b1d14');
      px(ctx, x - 2, 50, w + 4, 1, '#c79a5c');
    }
    // Wainscot
    px(ctx, 0, 58, W, WALL_BOTTOM - 58, '#7a4b2a');
    for (let x = 0; x < W; x += 6) px(ctx, x, 59, 1, WALL_BOTTOM - 59, '#6a3f22');
    px(ctx, 0, 58, W, 1, '#9a6a3f');
    // Booth backs along the windows
    for (const bx of [58, 128, 198]) {
      px(ctx, bx - 24, 54, 48, 3, '#b83a3a');
      px(ctx, bx - 24, 57, 48, WALL_BOTTOM - 57, '#8e2424');
      for (let x = bx - 22; x < bx + 24; x += 8) px(ctx, x, 58, 1, 12, '#7a1d1d');
    }
    // Counter on the right with stools
    px(ctx, 232, 60, 24, 4, '#efe2c4');
    px(ctx, 232, 64, 24, WALL_BOTTOM - 64, '#3a2a20');
    px(ctx, 232, 60, 24, 1, '#fff6e0');
    // Floor
    for (let y = WALL_BOTTOM; y < H; y += 4) {
      const band = (y - WALL_BOTTOM) / (H - WALL_BOTTOM);
      const tile = 8 + Math.round(band * 6);
      for (let x = -((y / 4) % 2) * (tile / 2); x < W; x += tile) {
        const odd = Math.floor(x / tile + y / 4) % 2 === 0;
        px(
          ctx,
          x,
          y,
          tile,
          4,
          mixColor(odd ? '#9c5a33' : '#86492a', '#000000', 0.18 - band * 0.18),
        );
      }
    }
    px(ctx, 0, WALL_BOTTOM, W, 2, '#3a2418');
    // A soft pool of light on the fighting floor
    const g = ctx.createRadialGradient(W / 2, FLOOR - 4, 10, W / 2, FLOOR - 4, 140);
    g.addColorStop(0, 'rgba(255,230,160,0.18)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, WALL_BOTTOM, W, H - WALL_BOTTOM);
  }

  private paintForeground(ctx: CanvasRenderingContext2D): void {
    // Booth tables in front of the spectators, and a vignette.
    for (const bx of [58, 128, 198]) {
      px(ctx, bx - 22, 70, 44, 3, '#d9b07a');
      px(ctx, bx - 22, 73, 44, 1, '#8a6238');
      px(ctx, bx - 2, 74, 4, WALL_BOTTOM - 74, '#3a3a3a');
      px(ctx, bx + 12, 66, 4, 4, '#b8c0c8');
      px(ctx, bx - 16, 65, 2, 5, '#7a3b12');
    }
    const v = ctx.createRadialGradient(W / 2, H / 2, 60, W / 2, H / 2, 170);
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.45)');
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, W, H);
  }

  private drawSign(ctx: CanvasRenderingContext2D): void {
    const words = ['WAFFLE', 'HOUSE'];
    const tile = 6;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(128, 30, 2, 128, 30, 30);
    g.addColorStop(0, 'rgba(255,210,63,0.35)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(100, 18, 56, 30);
    ctx.restore();
    words.forEach((word, r) => {
      const rowW = word.length * (tile + 1) - 1;
      const x0 = 128 - Math.floor(rowW / 2);
      for (let i = 0; i < word.length; i++) {
        const tx = x0 + i * (tile + 1);
        const ty = 21 + r * (tile + 2);
        px(ctx, tx, ty, tile, tile + 1, SIGN_YELLOW);
        px(ctx, tx, ty + tile, tile, 1, SIGN_EDGE);
        glyphRows(word[i] ?? ' ').forEach((row, gy) => {
          for (let gx = 0; gx < row.length; gx++)
            if (row[gx] === '#') px(ctx, tx + 1 + gx, ty + 1 + gy, 1, 1, '#141414');
        });
      }
    });
  }

  private drawJukebox(ctx: CanvasRenderingContext2D): void {
    const x = 4;
    const y = 50;
    px(ctx, x + 2, y, 16, 2, '#4a2a18');
    px(ctx, x, y + 2, 20, WALL_BOTTOM - y - 2, '#5a3320');
    px(ctx, x + 2, y + 4, 16, 10, '#1a1320');
    const hues = ['#ff5f5f', '#ffd23f', '#5fd3ff', '#b56bff', '#5fff9f'];
    for (let i = 0; i < 5; i++) {
      const c = hues[(i + Math.floor(this.t * 3)) % hues.length] ?? '#fff';
      px(ctx, x + 1, y + 3 + i * 6, 1, 5, c);
      px(ctx, x + 18, y + 3 + i * 6, 1, 5, c);
    }
    px(ctx, x + 9 + (Math.floor(this.t * 6) % 2), y + 8, 2, 2, '#ffd23f');
  }

  private drawCrowd(ctx: CanvasRenderingContext2D): void {
    for (const s of this.spectators) {
      const excited = this.hype > 0.5;
      const bob = Math.round(
        Math.abs(Math.sin(this.t * (excited ? 12 : 3) + s.phase)) * (excited ? 3 : 1),
      );
      const mood = excited ? (Math.floor(this.t * 4 + s.phase) % 2 ? 'talk' : 'happy') : 'normal';
      drawSprite(ctx, miniSprite(s.look, mood), s.x, s.y - bob);
      if (s.filming) {
        // Somebody's always filming.
        const phx = s.x + MINI_W - 2;
        const phy = s.y + 3 - bob;
        px(ctx, phx, phy, 4, 6, '#15151b');
        px(ctx, phx + 1, phy + 1, 2, 4, '#7fd6ff');
        if (Math.floor(this.t * 2) % 2 === 0) px(ctx, phx + 3, phy - 1, 1, 1, '#ff3b30');
      }
    }
  }

  private drawDottie(ctx: CanvasRenderingContext2D): void {
    // Dottie watches from behind the counter, arms folded, unimpressed.
    const sprite = dottieSprite(Math.floor(this.t * 0.7) % 5 === 0 ? 'blink' : 'normal');
    drawSprite(ctx, sprite, 234, 36);
    px(ctx, 232, 60, 24, 4, '#efe2c4');
    px(ctx, 232, 60, 24, 1, '#fff6e0');
    px(ctx, 232, 64, 24, WALL_BOTTOM - 64, '#3a2a20');
  }

  /* ───────────────────────── fighters ───────────────────────── */

  private drawShadow(ctx: CanvasRenderingContext2D, f: FighterState): void {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(Math.round(f.x), FLOOR, 12, 3, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private poseOf(bout: Bout, f: FighterState): { pose: FighterPose; frame: number } {
    const frame = Math.floor(this.t * 4);
    if (bout.phase === 'ko' || bout.phase === 'done') {
      const lost = (bout.outcome === 'calmed') === (f.side === 'opponent');
      return lost ? { pose: 'sit', frame } : { pose: 'victory', frame };
    }
    if (f.stun > 0 && !f.blocking) return { pose: 'hit', frame };
    if (f.parried > 0) return { pose: 'hit', frame };
    if (f.blocking) return { pose: 'block', frame };
    const a = f.action;
    if (a) {
      if (a.def.id === 'super' && bout.superSeq)
        return { pose: Math.floor(this.t * 16) % 2 ? 'super' : 'jab', frame };
      if (a.t < a.def.startup && a.def.reach > 0 && a.def.id !== 'super')
        return { pose: 'windup', frame };
      if (a.def.id === 'toss' || a.def.id === 'throw')
        return { pose: a.t < a.def.startup ? 'windup' : 'throw', frame };
      return { pose: a.def.pose, frame };
    }
    if (f.walking !== 0) return { pose: 'walk', frame: Math.floor(f.walkT * 8) };
    return { pose: 'idle', frame: Math.floor(this.t * 2) };
  }

  private drawFighter(ctx: CanvasRenderingContext2D, bout: Bout, f: FighterState): void {
    const look: SideLook = f.side === 'player' ? COOK : bout.opponentDef.side;
    const { pose, frame } = this.poseOf(bout, f);
    const lostCool = bout.phase !== 'intro' && f.side === 'player' && bout.outcome === 'lost';
    const angry = f.side === 'opponent' && bout.phase === 'fight' && f.meter / f.maxMeter > 0.5;
    const sprite = sideSprite(look, pose, frame, lostCool || (angry && pose === 'taunt'));
    const x = Math.round(f.x) - (f.facing === 1 ? ANCHOR_X : sprite.w - ANCHOR_X);
    const y = FLOOR - SIDE_H + 1;
    drawSprite(ctx, sprite, x, y, { flip: f.facing === -1 });

    // Telegraphs and hit flashes read at a glance.
    const a = f.action;
    const winding =
      f.side === 'opponent' &&
      a !== null &&
      a.t < a.def.startup &&
      (a.def.reach > 0 || a.def.id === 'throw');
    if (winding && Math.floor(this.t * 16) % 2 === 0) {
      drawSprite(ctx, tint(sprite, a.def.armor ? '#ff9f1c' : '#ff3b30'), x, y, {
        flip: f.facing === -1,
        alpha: 0.55,
      });
    }
    if (f.flash > 0)
      drawSprite(ctx, tint(sprite, '#ffffff'), x, y, { flip: f.facing === -1, alpha: 0.8 });
    if (
      f.side === 'player' &&
      bout.superReady &&
      bout.phase === 'fight' &&
      Math.floor(this.t * 6) % 2 === 0
    ) {
      drawSprite(ctx, tint(sprite, '#ffd23f'), x, y, { flip: f.facing === -1, alpha: 0.35 });
    }
    // Anger steam while they're still hot; hearts once they calm down.
    if (
      f.side === 'opponent' &&
      bout.phase === 'fight' &&
      f.meter / f.maxMeter > 0.35 &&
      Math.floor(this.t * 3) % 2 === 0
    ) {
      px(
        ctx,
        Math.round(f.x) - 2,
        FLOOR - SIDE_H - 2 - (Math.floor(this.t * 6) % 3),
        2,
        2,
        'rgba(255,255,255,0.6)',
      );
      px(
        ctx,
        Math.round(f.x) + 3,
        FLOOR - SIDE_H - 4 - (Math.floor(this.t * 5) % 3),
        2,
        2,
        'rgba(255,255,255,0.5)',
      );
    }
    if (lostCool && Math.floor(this.t * 4) % 2 === 0) {
      px(ctx, Math.round(f.x) - 3, FLOOR - 30, 2, 3, 'rgba(255,255,255,0.7)');
      px(ctx, Math.round(f.x) + 2, FLOOR - 32, 2, 3, 'rgba(255,255,255,0.7)');
    }
    if (bout.phase === 'ko' && f.side === 'opponent' && bout.outcome === 'calmed') {
      // A waffle materialises. They order it.
      const wobble = Math.round(Math.sin(this.t * 3));
      ctx.save();
      drawWaffle(ctx, Math.round(f.x) + f.facing * 14, FLOOR - 28 + wobble, 4, 'pecan', 1);
      ctx.restore();
    }
  }

  private drawProjectile(ctx: CanvasRenderingContext2D, p: Projectile): void {
    const x = Math.round(p.x);
    const spin = p.age * 14;
    const y = FLOOR - 26 + Math.round(Math.sin(p.age * 10) * (p.kind === 'yell' ? 1 : 0));
    switch (p.kind) {
      case 'hashBrown': {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(spin);
        px(ctx, -5, -3, 10, 6, '#e0a93f');
        px(ctx, -4, -4, 8, 8, '#e0a93f');
        for (let i = 0; i < 8; i++)
          px(ctx, -4 + ((i * 3) % 8), -3 + ((i * 5) % 6), 2, 1, i % 2 ? '#f6d27a' : '#b97a2c');
        ctx.restore();
        this.trail(p, '#ffd27a');
        break;
      }
      case 'bouquet': {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(spin * 0.6);
        px(ctx, -1, 0, 2, 7, '#2f7d32');
        px(ctx, -4, -4, 3, 3, '#ff5fa2');
        px(ctx, 0, -5, 3, 3, '#ffffff');
        px(ctx, 2, -2, 3, 3, '#ffd23f');
        ctx.restore();
        break;
      }
      case 'chair': {
        ctx.save();
        ctx.translate(x, y - 4);
        ctx.rotate(spin * 0.7);
        ctx.drawImage(this.chair.canvas, -6, -6);
        ctx.restore();
        this.trail(p, '#ffffff');
        break;
      }
      case 'yell': {
        const w = 18;
        px(ctx, x - w / 2, y - 6, w, 11, '#ffffff');
        px(ctx, x - w / 2 + 1, y - 7, w - 2, 1, '#ffffff');
        px(ctx, x - w / 2 + 1, y + 5, w - 2, 1, '#ffffff');
        px(ctx, x + (p.vx > 0 ? -w / 2 - 2 : w / 2), y + 2, 3, 2, '#ffffff');
        drawPixelText(ctx, '!!!', x - 5, y - 3, '#e5433b');
        break;
      }
      case 'shaker': {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(spin);
        px(ctx, -3, -6, 6, 12, '#f5f5f5');
        px(ctx, -3, -7, 6, 2, '#1e1e24');
        px(ctx, -3, -1, 6, 3, '#e74c3c');
        ctx.restore();
        break;
      }
      case 'record': {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(spin * 1.5);
        ctx.fillStyle = '#111111';
        ctx.beginPath();
        ctx.arc(0, 0, 6, 0, Math.PI * 2);
        ctx.fill();
        px(ctx, -2, -2, 4, 4, '#e05fb0');
        px(ctx, 0, 0, 1, 1, '#111');
        px(ctx, -5, -1, 2, 1, '#444');
        ctx.restore();
        break;
      }
    }
  }

  private trail(p: Projectile, color: string): void {
    if (this.reducedMotion) return;
    const dir = p.vx > 0 ? -1 : 1;
    for (let i = 1; i <= 3; i++) {
      this.ctx.globalAlpha = clamp(0.35 - i * 0.1, 0, 1);
      px(this.ctx, Math.round(p.x) + dir * i * 5, FLOOR - 26, 3, 1, color);
    }
    this.ctx.globalAlpha = 1;
  }
}
